<template>
    <div class="container">
        <h1 class="title is-1">Hydroseed Estimator</h1>
        <p class="subtitle">Change the values for instantly recalculated results</p>
        <div class="columns estimator">
            <div class="column is-one-third-desktop">

                <label class="label">Project & Equipment Details</label>
                <div>
                    <label class="label dimension-label">Total Project Size</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="number" v-model.number="totalProjectSize" :placeholder="projectSizeUnit.name">
                        </p>
                        <p class="control">
                            <span class="select">
                                <select :value="selectedProjectSizeUnit"
                                        @change="selectedProjectSizeUnit = $event.target.value">
                                    <option v-for="(unit, index) in projectSizeUnits" :value="index">{{ unit.name }}</option>
                                </select>
                            </span>
                        </p>
                    </div>
                </div>
                <div>
                    <label class="label dimension-label">Working Capacity of Tank</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="number" v-model.number="tankWorkingCapacity" placeholder="Tank size in gallons">
                        </p>
                        <p class="control">
                            <button :disabled="true" class="button is-light is-disabled">gallons</button>
                        </p>
                    </div>
                </div>

            </div>
            <div class="column is-one-third-desktop">

                <label class="label">Mulch Application Details</label>
                <div>
                    <label class="label dimension-label">Target Application Rate</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="number" v-model.number="mulchApplicationRate" placeholder="Pounds of mulch per acre">
                        </p>
                        <p class="control">
                            <button :disabled="true" class="button is-light is-disabled">lbs/acre</button>
                        </p>
                    </div>
                </div>
                <div>
                    <label class="label dimension-label">Weight of Mulch (per bag)</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="number" v-model.number="mulchBagWeight" placeholder="Weight per bag of mulch">
                        </p>
                        <p class="control">
                            <button :disabled="true" class="button is-light is-disabled">lbs</button>
                        </p>
                    </div>
                </div>
                <div>
                    <label class="label dimension-label">Mulch Mixing Rate</label>
                    <div class="field has-addons">
                        <p class="control">
                            <input class="input" type="number" v-model.number="mulchMixRate" placeholder="Pounds of mulch per 100 gallons">
                        </p>
                        <p class="control">
                            <button :disabled="true" class="button is-light is-disabled">lbs/100 gal</button>
                        </p>
                    </div>
                </div>

            </div>
            <div class="column is-one-third-desktop">
                <label class="label">Seed Application Details</label>
                <div v-if="seeds.length > 0" v-for="(seed, index) in seeds">
                    <div>
                        <div class="field has-addons">
                            <p class="control">
                                <input class="input label-input" type="text" v-model="seeds[index].name" placeholder="Seed Name">
                            </p>
                            <p class="control">
                                <button class="button is-danger" @click="removeSeed(index)">delete</button>
                            </p>
                        </div>
                    </div>
                    <div>
                        <label class="label dimension-label">Target Application Rate</label>
                        <div class="field has-addons">
                            <p class="control">
                                <input class="input" type="number" v-model.number="seeds[index].applicationRate" placeholder="Pounds of seed per acre">
                            </p>
                            <p class="control">
                                <button :disabled="true" class="button is-light is-disabled">lbs/acre</button>
                            </p>
                        </div>
                    </div>
                    <div>
                        <label class="label dimension-label">Weight of Seed (per bag)</label>
                        <div class="field has-addons">
                            <p class="control">
                                <input class="input" type="number" v-model.number="seeds[index].bagWeight" placeholder="Weight per bag of seed">
                            </p>
                            <p class="control">
                                <button :disabled="true" class="button is-light is-disabled">lbs</button>
                            </p>
                        </div>
                    </div>
                    <div>
                        <label class="label dimension-label">Seed Mixing Rate</label>
                        <div class="field has-addons">
                            <p class="control">
                                <input class="input" type="number" v-model.number="seeds[index].mixRate" placeholder="Pounds of seed per 100 gallons">
                            </p>
                            <p class="control">
                                <button :disabled="true" class="button is-light is-disabled">lbs/100 gal</button>
                            </p>
                        </div>
                    </div>
                    <hr v-if="seeds.length > 1 && index !== seeds.length - 1">
                </div>
                <div class="field">
                    <p class="control">
                        <button class="button is-primary" @click="addSeed()">Add seed type</button>
                    </p>
                </div>
            </div>
        </div>
        <div class="columns estimator">
            <div class="column is-full">
                <h1 class="title">Hydro Estimate:</h1>
                <div class="content">
                    <div class="is-large results">
                        Your project has an area of <strong>{{ results.area }}</strong> square feet.
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    export default {
        name: 'hydro-calculator',
        data () {
            return {
                precision: 1000,
                selectedProjectSizeUnit: 'acre',
                totalProjectSize: 1,
                tankWorkingCapacity: 500, // gallons
                projectSizeUnits: { // all project units in square feet
                    square_feet: {
                        name: 'Square Feet',
                        sqft: 1,
                    },
                    square_yard: {
                        name: 'Square Yards',
                        sqft: 9,
                    },
                    acre: {
                        name: 'Acres',
                        sqft: 43560,
                    },
                    hectare: {
                        name: 'Hectares',
                        sqft: 107639,
                    },
                },
                mulchApplicationRate: 2000, // lbs/acre
                mulchMixRate: 50, // lbs/100 gallons
                mulchBagWeight: 50, // lbs,
                seeds: [],
                seedTemplate: {
                    name: 'Generic Seed',
                    applicationRate: 90, // lbs/acre
                    mixRate: 2.3, // lbs/100 gallons
                    bagWeight: 50, // lbs
                }
            }
        },
        methods: {
            addSeed() {
                this.seeds.push(this.seedTemplate);
            },
            removeSeed(index) {
                this.seeds.splice(index, 1);
            }
        },
        computed: {
            projectSizeUnit() {
                return this.projectSizeUnits[this.selectedProjectSizeUnit];
            },
            results() {
                let area = Math.round((this.totalProjectSize * this.projectSizeUnit.sqft) * this.precision) / this.precision;

                return {
                    area: area,
                }
            }
        },
    }
</script>



// WEBPACK FOOTER //
// HydroCalculator.vue?2b2e001e